import { useQuery } from '@tanstack/react-query';
import { get } from '@/shared/api/api-client';

/**
 * Organisation feature flags.
 *
 *   GET /api/Organization/{organizationId}/Features
 *
 * Flutter parity: `permission_data_source_impl.dart` →
 * `GetOrganisationFeatureUseCase`, dispatched from `calendar_tab.dart`
 * (`GetOrganisationFeatureEvent`) on calendar-tab init and stored in the
 * SettingsBloc state (`OrganisationFeature? features`).
 *
 * **Fetch-but-don't-gate (verbatim Flutter behaviour):** Flutter fetches
 * this on calendar mount and stores it, but **no widget currently branches
 * on the flags** — it's infrastructure prepared for future billing/encounter
 * gating. We replicate that faithfully: the query primes the cache on the
 * scheduling page; nothing gates on it yet. When a real gate is needed
 * (e.g. `appointment.billing_profile_mandatory`, `encounter.drug_interaction`),
 * consume `useOrganisationFeatures(orgId).data` at that call-site.
 *
 * Wire shape is fully specified by the Flutter model (`organisation_feature.dart`)
 * — a no-body GET, so no curl was needed. JSON keys are snake_case verbatim
 * (`billing_profile_mandatory`, `drug_interaction`); kept as-is on the type.
 */

export interface AppointmentFeatureModule {
  billing_profile_mandatory?: boolean | null;
}

export interface EncounterFeatureModule {
  ros?: boolean | null;
  pe?: boolean | null;
  drug_interaction?: boolean | null;
}

export interface FeaturesInfo {
  encounter?: EncounterFeatureModule | null;
  appointment?: AppointmentFeatureModule | null;
}

export interface OrganisationFeature {
  web?: FeaturesInfo | null;
  mobile?: FeaturesInfo | null;
}

export function useOrganisationFeatures(
  organizationId: string | null | undefined,
) {
  return useQuery({
    queryKey: ['organisation-features', organizationId],
    enabled: !!organizationId,
    queryFn: ({ signal }) => {
      if (!organizationId) {
        throw new Error('useOrganisationFeatures called without organizationId');
      }
      return get<OrganisationFeature>(
        `/api/Organization/${organizationId}/Features`,
        { signal },
      );
    },
    // Org-scoped + rarely changes; cache aggressively (mirrors Flutter's
    // load-once-on-calendar-mount pattern — it never refetches per session).
    staleTime: 30 * 60 * 1000,
    gcTime: 60 * 60 * 1000,
    // Fetch-but-don't-gate infra fetch: a failure must NOT surface a toast
    // (the global QueryCache.onError) or break the calendar mount — nothing
    // consumes the result yet, so a silent miss is correct. `retry: false`
    // avoids spamming the endpoint; `skipGlobalError` opts out of the global
    // error toast (Samer review on PR #512 §4). `throwOnError` defaults false,
    // so the calendar render is never interrupted.
    retry: false,
    meta: { skipGlobalError: true },
  });
}
